import React from 'react'
import { createBrowserRouter, RouteObject, redirect } from 'react-router-dom'
import { requireSessionLoader, undoRouteLoader, chatbotGuardLoader, stepJumpLoader, faqGuardLoader } from './guards'

import Layout from '@/components/Layout'
import { portalAuthedGuardLoader, portalLoginGuardLoader } from '@/portals/order-management/routeGuards'
import { marketplaceGuardLoader } from '@/portals/marketplace/routeGuards'
import OmpLayout from '@/portals/order-management/OmpLayout'
import OmpLogin from '@/portals/order-management/pages/OmpLogin'
import OmpLogout from '@/portals/order-management/pages/OmpLogout'
import { OMP_PAGE_LOADERS } from '@/portals/order-management/pageLoaders'
import { NotFoundPage, RouterErrorBoundary } from './RouterFallbacks'

const OmpHome = React.lazy(OMP_PAGE_LOADERS.home)
const OmpSubscriptions = React.lazy(OMP_PAGE_LOADERS.subscriptions)
const OmpSubscriptionDetails = React.lazy(OMP_PAGE_LOADERS.subscriptionDetails)
const OmpOrders = React.lazy(OMP_PAGE_LOADERS.orders)
const OmpOrderDetails = React.lazy(OMP_PAGE_LOADERS.orderDetails)
const OmpAccount = React.lazy(OMP_PAGE_LOADERS.account)
const OmpAddresses = React.lazy(OMP_PAGE_LOADERS.addresses)
const OmpPaymentMethods = React.lazy(OMP_PAGE_LOADERS.paymentMethods)

const SessionMainPage = React.lazy(() => import('@/pages/SessionMainPage'))
const MatchPage = React.lazy(() => import('@/pages/MatchPage'))
const SessionJumpPage = React.lazy(() => import('@/pages/SessionJumpPage'))
const UndoPage = React.lazy(() => import('@/pages/UndoPage'))
const ChatbotPage = React.lazy(() => import('@/pages/ChatbotPage'))
const ChatbotEmbedPage = React.lazy(() => import('@/components/ChatbotEmbed'))
const AuthTokenPage = React.lazy(() => import('@/pages/AuthTokenPage'))
const LogoutPage = React.lazy(() => import('@/pages/LogoutPage'))
const ProductsPage = React.lazy(() => import('@/portals/marketplace/Products'))
const ProductDetailsPage = React.lazy(() => import('@/portals/marketplace/ProductDetails'))

// FAQ components
const FaqTopics = React.lazy(() => import('@/components/Faq/FaqTopics'))
const Faqs = React.lazy(() => import('@/components/Faq/Faqs'))
const FaqDetails = React.lazy(() => import('@/components/Faq/FaqDetails'))
const FaqSearch = React.lazy(() => import('@/components/Faq/FaqSearch'))

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      // Main session page — also handles session initialization
      {
        index: true,
        element: <SessionMainPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // Customer match / login
      {
        path: 'match',
        element: <MatchPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // /session/start — redirect to / (session start)
      {
        path: 'session/start',
        loader: ({ request }) => {
          const url = new URL(request.url)
          const dest = new URL('/', url.origin)
          url.searchParams.forEach((v, k) => dest.searchParams.set(k, v))
          throw redirect(dest.toString())
        },
        element: null,
      },
      {
        path: 'session/start/:step',
        loader: ({ params }) => {
          throw redirect(`/start/${params.step}`)
        },
        element: null,
      },
      // /session and /session/:step share the same route so navigating between
      // them does NOT remount SessionMainPage (which would refetch /order_items/).
      {
        path: 'session/*',
        element: <SessionMainPage />,
        loader: requireSessionLoader,
        errorElement: <RouterErrorBoundary />,
      },
      // Deep-link jump to a specific step — requires valid session + can_jump
      // If no session, redirect to /start/:step to start a new session and jump
      {
        path: 'step/:step',
        element: <SessionJumpPage />,
        loader: stepJumpLoader,
        errorElement: <RouterErrorBoundary />,
      },
      // Start a new session and jump to a specific step
      {
        path: 'start/:step',
        element: <SessionMainPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // Undo the last action
      {
        path: 'undo',
        element: <UndoPage />,
        loader: undoRouteLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'undo/:step',
        element: <UndoPage />,
        loader: undoRouteLoader,
        errorElement: <RouterErrorBoundary />,
      },
      // AuthToken routes
      {
        path: 'auth/:uid/:token',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'auth/:uid/:token/:step',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'auth/:uid/:token/start/:start',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // Products routes — gated by show_marketplace
      {
        path: 'products',
        element: <ProductsPage />,
        loader: marketplaceGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'products/:id/:slug',
        element: <ProductDetailsPage />,
        loader: marketplaceGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'chat',
        element: <ChatbotPage />,
        loader: chatbotGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'chatbot_embed',
        element: <ChatbotEmbedPage />,
        loader: chatbotGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ':uidToken',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ':uidToken/start/:start',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ':uidToken/step/:step',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // FAQ routes — gated by show_faqs
      {
        path: 'topics',
        element: <FaqTopics />,
        loader: faqGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'faqs',
        element: <Faqs />,
        loader: faqGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'faqs/search',
        element: <FaqSearch />,
        loader: faqGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'faqs/:slug',
        element: <FaqDetails />,
        loader: faqGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'logout',
        element: <LogoutPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
  // Order Management Portal — dedicated shell + routes, entered when the
  // session's campaign has subdomain_type === CustomerPortal (4).
  // Login (customer match) and logout both live outside the authed shell.
  {
    path: '/portal/login',
    element: <OmpLogin />,
    loader: portalLoginGuardLoader,
    errorElement: <RouterErrorBoundary />,
  },
  // No loader, for the same reason the funnel's /logout has none: every route here is
  // reached *after* the session is gone, so any session guard would bounce it to '/'.
  {
    path: '/portal/logout',
    element: <OmpLogout />,
    errorElement: <RouterErrorBoundary />,
  },
  {
    path: '/portal',
    element: <OmpLayout />,
    loader: portalAuthedGuardLoader,
    errorElement: <RouterErrorBoundary />,
    children: [
      { index: true, element: <OmpHome /> },
      { path: 'subscriptions', element: <OmpSubscriptions /> },
      { path: 'subscriptions/:id', element: <OmpSubscriptionDetails /> },
      { path: 'orders', element: <OmpOrders /> },
      { path: 'orders/:id', element: <OmpOrderDetails /> },
      { path: 'account', element: <OmpAccount /> },
      { path: 'account/addresses', element: <OmpAddresses /> },
      { path: 'account/payment', element: <OmpPaymentMethods /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export const router = createBrowserRouter(routes)
export default router
